// การลาของทันตแพทย์ — pure helpers for the doctor-leave tab. No I/O.
//
// ⚠️ THIS IS NOT `employee_timetable`. Employee leave already exists as
// employee_timetable rows of type ลากิจ/ลาป่วย measured against
// Employee.leavequota (see ./leave.js). Doctors are not on that table, have no
// quota, and their "shift" is an `appointment` row — so doctor leave is its own
// table (`doctor_leave`). The vocabulary and the chip colours are shared on
// purpose; the storage is not, and must not be merged.
//
// 🚨 Approving leave NEVER writes to `appointment`. The conflict panel exists to
// SHOW what a leave window collides with — including patients already booked —
// and ยกเลิกเวรวันนี้ด้วย is a separate, deliberate action. Owner's decision,
// 2026-08-20: approving leave must never silently remove a shift a patient is
// booked into.

export const LEAVE_STATUS = {
  pending:   { label: 'รอยืนยัน',  tone: 'amber' },
  approved:  { label: 'อนุมัติ',   tone: 'emerald' },
  rejected:  { label: 'ไม่อนุมัติ', tone: 'rose' },
  cancelled: { label: 'ยกเลิกแล้ว', tone: 'slate' },
}

const TONE_CLASS = {
  emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
  amber:   'bg-amber-50 text-amber-700 ring-amber-100',
  rose:    'bg-rose-50 text-rose-700 ring-rose-100',
  slate:   'bg-slate-100 text-slate-600 ring-slate-200',
}
export const toneClass = (tone) => TONE_CLASS[tone] || TONE_CLASS.slate
export const leaveStatusMeta = (s) => LEAVE_STATUS[s] || { label: s || '—', tone: 'slate' }

export const LEAVE_TYPES = [
  { value: 'full_day', label: 'ลาทั้งวัน' },
  { value: 'partial',  label: 'ลาบางช่วง' },
]

/* ── the partial-leave window ────────────────────────────────────────────── */

// A partial leave stores the ABSENT window directly, because the shift it
// attaches to may not exist yet — there is nothing to measure "late" against.
// Three shapes, and only one sets both bounds:
//   มาสาย     end_time only     — absent until HH:MM
//   ออกก่อน    start_time only   — absent from HH:MM
//   ช่วงเวลา   both              — absent between them
export const PARTIAL_MODES = [
  { value: 'late',   label: 'มาสาย',    hint: 'เข้างานช้ากว่าเวลาเวร' },
  { value: 'early',  label: 'ออกก่อน',  hint: 'ออกก่อนเวลาเวรเลิก' },
  { value: 'window', label: 'ช่วงเวลา', hint: 'ไม่อยู่ระหว่างเวลาที่ระบุ' },
]

export function partialMode(l) {
  const s = String(l?.start_time || '').trim()
  const e = String(l?.end_time || '').trim()
  if (s && e) return 'window'
  if (e) return 'late'
  if (s) return 'early'
  return 'window'
}

// Build the stored pair from the mode the doctor actually picked. Returning both
// keys always (even as '') matters: the Xano PATCH clears a text field on an
// empty string, so switching มาสาย → ออกก่อน has to blank the other bound or the
// row ends up claiming both.
export function partialWindow(mode, { start = '', end = '' } = {}) {
  if (mode === 'late')  return { start_time: '', end_time: end }
  if (mode === 'early') return { start_time: start, end_time: '' }
  return { start_time: start, end_time: end }
}

export function leaveWindowLabel(l) {
  if (!l || l.type !== 'partial') return 'ทั้งวัน'
  const s = String(l.start_time || '').trim()
  const e = String(l.end_time || '').trim()
  if (s && e) return `${s}–${e}`
  if (e) return `มาสาย · เข้างาน ${e}`
  if (s) return `ออกก่อน · ออก ${s}`
  return 'บางช่วง'
}

/* ── dates ───────────────────────────────────────────────────────────────── */

// Leave dates are plain 'YYYY-MM-DD' Bangkok calendar days, the same type and
// meaning as appointment.date. Never derive a day from a timestamp here — that
// is the tz-shift bug appointment-table.md warns about twice.
export const dayOnly = (v) => String(v || '').slice(0, 10)

export function leaveDayCount(l) {
  const a = dayOnly(l?.date_from)
  const b = dayOnly(l?.date_to) || a
  if (!a) return 0
  const [y1, m1, d1] = a.split('-').map(Number)
  const [y2, m2, d2] = b.split('-').map(Number)
  if (!y1 || !y2) return 0
  const t1 = Date.UTC(y1, m1 - 1, d1)
  const t2 = Date.UTC(y2, m2 - 1, d2)
  return Math.max(1, Math.round((t2 - t1) / 86400000) + 1)
}

const TH_MONTHS_ABBR = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']

// ค.ศ. on screen — the patient module's rule (พ.ศ. is for printed documents only).
export function fmtDay(v) {
  const s = dayOnly(v)
  if (!s) return '—'
  const [y, m, d] = s.split('-').map(Number)
  if (!y) return s
  return `${d} ${TH_MONTHS_ABBR[m - 1] || m} ${y}`
}

export function leaveDateLabel(l) {
  const a = dayOnly(l?.date_from)
  const b = dayOnly(l?.date_to) || a
  if (!a) return '—'
  if (a === b) return fmtDay(a)
  return `${fmtDay(a)} – ${fmtDay(b)}`
}

/* ── conflicts ───────────────────────────────────────────────────────────── */

// Shapes the GET /doctor_leave/{id} `conflicts` payload for display. The number
// that matters is `patient_count`: an approved leave over a day with patients
// booked is the failure nobody else in the clinic will notice, so it is stated
// first and separately from the shift rows.
export function conflictSummary(c) {
  const shifts = Array.isArray(c?.shifts) ? c.shifts : []
  const patients = Number(c?.patient_count || 0)
  return {
    shifts,
    shiftCount: shifts.length,
    patientCount: patients,
    hasAny: shifts.length > 0 || patients > 0,
    // Deliberately two separate sentences: a shift with no patients is an
    // administrative tidy-up, a shift WITH patients is a phone call to make.
    shiftNote: shifts.length > 0
      ? `มีเวรอยู่แล้ว ${shifts.length} ช่วงในช่วงวันที่ลา`
      : 'ไม่มีเวรในตารางช่วงวันที่ลา',
    patientNote: patients > 0
      ? `มีคนไข้จองไว้ ${patients} ราย — ต้องติดต่อเลื่อนนัดก่อน`
      : '',
    truncated: Boolean(c?.truncated),
  }
}

// A shift row inside the conflict panel: date + branch + window, using the same
// epoch-ms convention as everything else that reads `appointment`.
const pad = (n) => String(n).padStart(2, '0')
export function shiftTime(ms) {
  if (ms == null || ms === '') return ''
  const d = new Date(Number(ms))
  if (Number.isNaN(d.getTime())) return ''
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`
}
export function shiftLabel(s) {
  if (!s) return '—'
  if (s.all_day) return 'ทั้งวัน'
  const a = shiftTime(s.start_date)
  const b = shiftTime(s.end_date)
  return a && b ? `${a}–${b}` : (a || b || '—')
}

/* ── form validation (mirrors the Xano preconditions) ────────────────────── */

// Returns a Thai error string, or '' when the form is sendable. Mirrored in
// Xano — this only shapes the screen so the user is not told "no" after a round
// trip; the server is what actually decides.
export function validateLeave(form) {
  const from = dayOnly(form?.date_from)
  if (!from) return 'กรุณาเลือกวันที่เริ่มลา'
  if (form?.type === 'partial') {
    const s = String(form.start_time || '').trim()
    const e = String(form.end_time || '').trim()
    if (!s && !e) return 'กรุณาระบุเวลาที่ลา'
    if (s && e && e <= s) return 'เวลาสิ้นสุดต้องอยู่หลังเวลาเริ่ม'
    return ''
  }
  const to = dayOnly(form?.date_to) || from
  if (to < from) return 'วันสิ้นสุดต้องไม่อยู่ก่อนวันเริ่ม'
  return ''
}

// A partial leave is always a single day — a "leave at 14:00" spanning three
// days has no meaning, and allowing it would make both the conflict panel and
// the roster overlap unreadable. Xano forces this too; doing it here keeps the
// date_to input from showing a value the server is about to discard.
export function normaliseLeave(form) {
  const from = dayOnly(form?.date_from)
  const isPartial = form?.type === 'partial'
  return {
    type: isPartial ? 'partial' : 'full_day',
    date_from: from,
    date_to: isPartial ? from : (dayOnly(form?.date_to) || from),
    start_time: isPartial ? String(form.start_time || '').trim() : '',
    end_time: isPartial ? String(form.end_time || '').trim() : '',
    reason: String(form?.reason || '').trim(),
  }
}
